import { collection, query, where, getDocs, Timestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * Fetch organization_visited events for a given org and date range.
 * Returns { totalVisits, uniqueVisitors }
 */
export const fetchOrganizationVisits = async (organizationId, startDate, endDate) => {
  try {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    console.log("=== fetchOrganizationVisits ===");
    console.log("organizationId:", organizationId);
    console.log("startDate:", startDate.toISOString());
    console.log("endDate:", endDate.toISOString());

    const q = query(
      collection(db, "userActivity"),
      where("eventName", "==", "organization_visited"),
      where("eventData.organizationId", "==", organizationId),
      where("eventTimestamp", ">=", startTimestamp),
      where("eventTimestamp", "<=", endTimestamp)
    );

    const snapshot = await getDocs(q);
    console.log("organization_visited results:", snapshot.size);
    
    if (snapshot.size === 0) {
      // Debug: try a simpler query to see if any data exists at all
      console.log("No results. Trying simpler query (eventName only)...");
      const simpleQ = query(
        collection(db, "userActivity"),
        where("eventName", "==", "organization_visited"),
        where("eventData.organizationId", "==", organizationId)
      );
      const simpleSnap = await getDocs(simpleQ);
      console.log("Simple query (no date filter) results:", simpleSnap.size);
      
      if (simpleSnap.size > 0) {
        // Log the first doc to see its timestamp
        const firstDoc = simpleSnap.docs[0].data();
        console.log("First doc eventTimestamp:", firstDoc.eventTimestamp);
        console.log("First doc eventTimestamp as Date:", firstDoc.eventTimestamp?.toDate?.());
      } else {
        // Even simpler - just check if any userActivity docs exist
        console.log("Still no results. Trying query with just eventName...");
        const eventOnlyQ = query(
          collection(db, "userActivity"),
          where("eventName", "==", "organization_visited")
        );
        const eventOnlySnap = await getDocs(eventOnlyQ);
        console.log("eventName-only query results:", eventOnlySnap.size);
        if (eventOnlySnap.size > 0) {
          const firstDoc = eventOnlySnap.docs[0].data();
          console.log("First doc eventData.organizationId:", firstDoc.eventData?.organizationId);
          console.log("We're looking for organizationId:", organizationId);
        }
      }
    }

    const totalVisits = snapshot.size;
    const uniqueUserIds = new Set();
    const visitsByDay = {};
    const uniqueVisitorsByDay = {}; // { [dateKey]: Set<userId> }

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.userId) {
        uniqueUserIds.add(data.userId);
      }

      // Group visits by day (local date) for the per-day chart
      const ts = data.eventTimestamp;
      const date = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
      if (date) {
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        visitsByDay[dateKey] = (visitsByDay[dateKey] || 0) + 1;

        // Track unique visitors per day
        if (data.userId) {
          if (!uniqueVisitorsByDay[dateKey]) {
            uniqueVisitorsByDay[dateKey] = new Set();
          }
          uniqueVisitorsByDay[dateKey].add(data.userId);
        }
      }
    });

    // Convert the per-day unique visitor Sets into counts
    const uniqueVisitorsByDayCount = {};
    Object.keys(uniqueVisitorsByDay).forEach((dateKey) => {
      uniqueVisitorsByDayCount[dateKey] = uniqueVisitorsByDay[dateKey].size;
    });

    return {
      totalVisits,
      uniqueVisitors: uniqueUserIds.size,
      visitsByDay,
      uniqueVisitorsByDay: uniqueVisitorsByDayCount,
    };
  } catch (error) {
    console.error("Error fetching organization visits:", error);
    console.error("Error details:", error.code, error.message);
    return { totalVisits: 0, uniqueVisitors: 0, visitsByDay: {}, uniqueVisitorsByDay: {} };
  }
};

/**
 * Fetch click_content_card events for quicktales for a given org and date range.
 * Returns an array of { objectId, contentName, count } sorted descending by count,
 * a map of clicks per day { [dateString]: count } (all quicktales combined),
 * and a nested map of clicks per day per quicktale { [objectId]: { [dateString]: count } }.
 */
export const fetchContentCardClicks = async (organizationId, startDate, endDate) => {
  try {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    console.log("=== fetchContentCardClicks ===");
    console.log("organizationId:", organizationId);
    console.log("startDate:", startDate.toISOString());
    console.log("endDate:", endDate.toISOString());

    const q = query(
      collection(db, "userActivity"),
      where("eventName", "==", "click_content_card"),
      where("eventData.organizationId", "==", organizationId),
      where("eventData.object", "==", "quicktale"),
      where("eventTimestamp", ">=", startTimestamp),
      where("eventTimestamp", "<=", endTimestamp)
    );

    const snapshot = await getDocs(q);
    console.log("click_content_card results:", snapshot.size);

    if (snapshot.size === 0) {
      // Debug: try without date filter
      console.log("No results. Trying without date filter...");
      const simpleQ = query(
        collection(db, "userActivity"),
        where("eventName", "==", "click_content_card"),
        where("eventData.organizationId", "==", organizationId),
        where("eventData.object", "==", "quicktale")
      );
      const simpleSnap = await getDocs(simpleQ);
      console.log("Without date filter results:", simpleSnap.size);
      
      if (simpleSnap.size > 0) {
        const firstDoc = simpleSnap.docs[0].data();
        console.log("First click doc eventTimestamp:", firstDoc.eventTimestamp);
        console.log("First click doc eventTimestamp as Date:", firstDoc.eventTimestamp?.toDate?.());
      }
    }

    // Group by objectId for per-quicktale counts
    const clicksByObjectId = {};
    // Group by day for daily totals (all quicktales combined)
    const clicksByDay = {};
    // Group by day, broken down per quicktale: { [objectId]: { [dateKey]: count } }
    const clicksByDayPerQuickTale = {};

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const objectId = data.eventData?.objectId;
      const contentName = data.eventData?.contentName || "Unknown";

      // Per quicktale
      if (objectId) {
        if (!clicksByObjectId[objectId]) {
          clicksByObjectId[objectId] = { objectId, contentName, count: 0 };
        }
        clicksByObjectId[objectId].count += 1;
      }

      // Per day
      const eventTimestamp = data.eventTimestamp;
      if (eventTimestamp) {
        const date = eventTimestamp.toDate();
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        clicksByDay[dateKey] = (clicksByDay[dateKey] || 0) + 1;

        // Per day, per quicktale
        if (objectId) {
          if (!clicksByDayPerQuickTale[objectId]) {
            clicksByDayPerQuickTale[objectId] = {};
          }
          clicksByDayPerQuickTale[objectId][dateKey] =
            (clicksByDayPerQuickTale[objectId][dateKey] || 0) + 1;
        }
      }
    });

    // Sort per-quicktale by count descending
    const perQuickTale = Object.values(clicksByObjectId).sort((a, b) => b.count - a.count);

    return { perQuickTale, clicksByDay, clicksByDayPerQuickTale };
  } catch (error) {
    console.error("Error fetching content card clicks:", error);
    console.error("Error details:", error.code, error.message);
    return { perQuickTale: [], clicksByDay: {}, clicksByDayPerQuickTale: {} };
  }
};

/**
 * Fetch QuickTale titles from the quicktales collection given an array of IDs.
 * Returns a map of { [id]: title }
 */
export const fetchQuickTaleTitles = async (objectIds) => {
  const titleMap = {};
  if (!objectIds || objectIds.length === 0) return titleMap;

  try {
    // Firestore 'in' queries support max 30 items at a time
    const batchSize = 30;
    for (let i = 0; i < objectIds.length; i += batchSize) {
      const batch = objectIds.slice(i, i + batchSize);
      
      // Fetch each doc individually since we have doc IDs
      const promises = batch.map(async (id) => {
        try {
          const docRef = doc(db, "quicktales", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            titleMap[id] = docSnap.data().title || "Untitled";
          } else {
            titleMap[id] = "Unknown QuickTale";
          }
        } catch (err) {
          console.error(`Error fetching quicktale ${id}:`, err);
          titleMap[id] = "Unknown QuickTale";
        }
      });

      await Promise.all(promises);
    }
  } catch (error) {
    console.error("Error fetching quicktale titles:", error);
  }

  return titleMap;
};

/**
 * Normalize a selected date range for querying.
 * Start is set to 00:00:00.000; end is set to 23:59:59.999,
 * but capped at "now" if the end date is today (or in the future).
 * Returns { startDate, endDate } or null if the range is incomplete.
 */
export const normalizeRange = (start, end) => {
  if (!start || !end) return null;

  const startDate = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    0,
    0,
    0,
    0
  );

  const now = new Date();
  let endDate = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
    23,
    59,
    59,
    999
  );

  if (endDate > now) {
    endDate = now;
  }

  return { startDate, endDate };
};

/**
 * Build a human-readable label for a date range,
 * e.g. "Aug 1 – Aug 19, 2026" or "Dec 15, 2025 – Jan 3, 2026".
 */
export const formatRangeLabel = (start, end) => {
  if (!start || !end) return "";

  const sameYear = start.getFullYear() === end.getFullYear();
  const startOpts = sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  const endOpts = { month: "short", day: "numeric", year: "numeric" };

  const startStr = start.toLocaleDateString("en-US", startOpts);
  const endStr = end.toLocaleDateString("en-US", endOpts);

  return `${startStr} – ${endStr}`;
};
